import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import EventBus from 'eventing-bus';
import html2canvas from 'html2canvas';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Modal, ModalBody, ModalHeader, Form, Alert } from 'reactstrap';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Box, TextField, Typography, FormControl, Select, MenuItem, Checkbox, ListItemIcon, CircularProgress } from '@mui/material';
import { ApiUrl } from '../store/config';

const AddDealModal = () => {
  const dispatch = useDispatch();
  const { userDetails } = useSelector(state => state.Settings);
  const userEmail = userDetails?.email || '';

  const [othersTab, setOtherTab] = useState(false);
  const [issuesTab, setIssuesTab] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState(false);
  const [suggestionsTab, setSuggestionsTab] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    issue: { title: '', description: '', email: userEmail, screenshot: null },
    suggestion: { title: '', description: '', email: userEmail, screenshot: null },
    others: { title: '', description: '', email: userEmail, screenshot: null }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [previewImage, setPreviewImage] = useState({ issue: null, suggestion: null, others: null });
  const [isCapturing, setIsCapturing] = useState(false);
  const [showSelectionOverlay, setShowSelectionOverlay] = useState(false);
  const [currentCaptureType, setCurrentCaptureType] = useState(null);

  // Update email when user details load
  useEffect(() => {
    if (userEmail) {
      setFormData(prev => ({
        issue: { ...prev.issue, email: userEmail },
        suggestion: { ...prev.suggestion, email: userEmail },
        others: { ...prev.others, email: userEmail }
      }));
    }
  }, [userEmail]);

  // Handle form field changes
  const handleInputChange = (type, field, value) => {
    setFormData(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: value }
    }));
  };

  // Convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Handle file upload
  const handleFileUpload = async (type, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      EventBus.publish('error', 'Please upload only PNG, JPG, or GIF images');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      EventBus.publish('error', 'File size must be less than 5MB');
      return;
    }

    try {
      const base64 = await convertToBase64(file);
      setFormData(prev => ({
        ...prev,
        [type]: { ...prev[type], screenshot: base64 }
      }));
      setPreviewImage(prev => ({ ...prev, [type]: base64 }));
    } catch (error) {
      EventBus.publish('error', 'Error processing image');
    }
  };

  // Remove uploaded image
  const removeImage = (type) => {
    setFormData(prev => ({
      ...prev,
      [type]: { ...prev[type], screenshot: null }
    }));
    setPreviewImage(prev => ({ ...prev, [type]: null }));
    // Reset file input
    const fileInput = document.getElementById(`file-upload-input-${type}`);
    if (fileInput) fileInput.value = '';
  };

  // Capture screenshot with immediate area selection
  const captureScreenshot = (type) => {
    setCurrentCaptureType(type);
    setFeedbackModal(false); // Hide modal temporarily

    // Small delay to ensure modal is hidden before showing overlay
    setTimeout(() => {
      setShowSelectionOverlay(true);
    }, 100);
  };

  // Handle selection complete
  const handleSelectionComplete = async (selectedArea) => {
    try {
      setShowSelectionOverlay(false);
      setIsCapturing(true);

      // Capture the entire window using html2canvas
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        logging: false,
        scale: 1
      });

      // Crop the selected area
      const croppedCanvas = document.createElement('canvas');
      croppedCanvas.width = selectedArea.width;
      croppedCanvas.height = selectedArea.height;
      const ctx = croppedCanvas.getContext('2d');

      ctx.drawImage(
        canvas,
        selectedArea.x,
        selectedArea.y,
        selectedArea.width,
        selectedArea.height,
        0,
        0,
        selectedArea.width,
        selectedArea.height
      );

      // Convert to base64
      const base64 = croppedCanvas.toDataURL('image/png');

      // Validate size (5MB limit)
      const base64Length = base64.length;
      const sizeInBytes = (base64Length * 3) / 4;
      const sizeInMB = sizeInBytes / (1024 * 1024);

      if (sizeInMB > 5) {
        EventBus.publish('error', 'Screenshot size exceeds 5MB. Please select a smaller area.');
        setIsCapturing(false);
        setFeedbackModal(true);
        return;
      }

      // Update state
      const type = currentCaptureType;
      setFormData(prev => ({
        ...prev,
        [type]: { ...prev[type], screenshot: base64 }
      }));
      setPreviewImage(prev => ({ ...prev, [type]: base64 }));

      setIsCapturing(false);
      setFeedbackModal(true); // Show modal again
      setCurrentCaptureType(null);

      EventBus.publish('success', 'Screenshot captured successfully!');
    } catch (error) {
      console.error('Screenshot capture error:', error);
      EventBus.publish('error', 'Failed to capture screenshot');
      setIsCapturing(false);
      setFeedbackModal(true);
    }
  };

  // Cancel selection
  const handleSelectionCancel = () => {
    setShowSelectionOverlay(false);
    setCurrentCaptureType(null);
    setFeedbackModal(true); // Show modal again
  };

  // Submit feedback
  const handleSubmit = async (type) => {
    const data = formData[type];

    // Validate required fields
    if (!data.title || !data.description) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${ApiUrl}/user/feedback`,
        {
          type,
          title: data.title,
          description: data.description,
          email: data.email || userEmail,
          screenshot: data.screenshot,
          // Add browser info
          browser: navigator.userAgent,
          platform: navigator.platform,
          screenResolution: `${window.screen.width}x${window.screen.height}`
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.code === 200) {
        const receivedTicketId = response.data.body.ticketId;

        // Show success message immediately
        EventBus.publish('success', `Feedback submitted successfully! Ticket ID: ${receivedTicketId}`);

        // Close modal immediately
        setFeedbackModal(false);
        setIssuesTab(false);
        setSuggestionsTab(false);
        setOtherTab(false);

        // Reset form data after modal closes
        setTimeout(() => {
          setFormData({
            issue: { title: '', description: '', email: userEmail, screenshot: null },
            suggestion: { title: '', description: '', email: userEmail, screenshot: null },
            others: { title: '', description: '', email: userEmail, screenshot: null }
          });
          setPreviewImage({ issue: null, suggestion: null, others: null });
          setSubmitSuccess(false);
          setSubmitError('');
        }, 500);
      } else {
        setSubmitError(response.data.message || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      setSubmitError(error.response?.data?.message || 'Failed to submit feedback. Please try again.');
      EventBus.publish('error', error.response?.data?.message || 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset states when modal closes
  const closeModal = () => {
    setFeedbackModal(false);
    setIssuesTab(false);
    setSuggestionsTab(false);
    setOtherTab(false);
    setSubmitError('');
    setSubmitSuccess(false);
  };

  return (
    <>
      <button className='feedback-button btn-style-border' onClick={() => setFeedbackModal(true)}>
        <i className='icon'>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bug w-5 h-5 mr-2 group-hover:scale-110 transition-transform"><path d="m8 2 1.88 1.88"></path><path d="M14.12 3.88 16 2"></path><path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1"></path><path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6"></path><path d="M12 20v-9"></path><path d="M6.53 9C4.6 8.8 3 7.1 3 5"></path><path d="M6 13H2"></path><path d="M3 21c0-2.1 1.7-3.9 3.8-4"></path><path d="M20.97 5c0 2.1-1.6 3.8-3.5 4"></path><path d="M22 13h-4"></path><path d="M17.2 17c2.1.1 3.8 1.9 3.8 4"></path></svg>
        </i>
        <p>Send feedback</p>
        <div className='overlaybox-btn'>
          Send feedback
        </div>
      </button>
      <Modal isOpen={feedbackModal} className={`main-modal new-user-modal modal-dialog-centered create-label-modal`}>
        <ModalHeader toggle={closeModal}></ModalHeader>
        <ModalBody>
          <div className='filters-modal-content bug-modal-show'>
            <div className='tabs-area'>
              {/* --- Issue Form --- */}
              {issuesTab ? (
                <div className="bug-modal">
                  <div className="title-area">
                    <h3>
                      <button className="back-btn-issue" onClick={() => { setIssuesTab(false); setSubmitError(''); setSubmitSuccess(false); }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-right w-4 h-4 rotate-180"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
                      </button>
                      <i className='icon'>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bug w-6 h-6 text-primary" data-lov-id="src/components/BugReportWidget.tsx:160:24" data-lov-name="IconComponent" data-component-path="src/components/BugReportWidget.tsx" data-component-line="160" data-component-file="BugReportWidget.tsx" data-component-name="IconComponent" data-component-content="%7B%22className%22%3A%22w-6%20h-6%20text-primary%22%7D"><path d="m8 2 1.88 1.88"></path><path d="M14.12 3.88 16 2"></path><path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1"></path><path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6"></path><path d="M12 20v-9"></path><path d="M6.53 9C4.6 8.8 3 7.1 3 5"></path><path d="M6 13H2"></path><path d="M3 21c0-2.1 1.7-3.9 3.8-4"></path><path d="M20.97 5c0 2.1-1.6 3.8-3.5 4"></path><path d="M22 13h-4"></path><path d="M17.2 17c2.1.1 3.8 1.9 3.8 4"></path></svg>
                      </i>
                      Issue
                    </h3>
                  </div>
                  <div className="form-area">
                    <Form>
                      <div className="group-form">
                        <label>Bug Title *</label>
                        <TextField
                          fullWidth
                          hiddenLabel
                          size="small"
                          name='bug'
                          variant="standard"
                          placeholder="Brief summary of the issue"
                          value={formData.issue.title}
                          onChange={(e) => handleInputChange('issue', 'title', e.target.value)}
                        />
                      </div>
                      <div className="group-form">
                        <label>Description *</label>
                        <TextField
                          fullWidth
                          multiline
                          rows="3"
                          hiddenLabel
                          size="small"
                          name='description'
                          variant="standard"
                          placeholder="Please describe the issue in detail"
                          value={formData.issue.description}
                          onChange={(e) => handleInputChange('issue', 'description', e.target.value)}
                        />
                      </div>
                      <div className="group-form">
                        <label>Email (optional)</label>
                        <TextField
                          fullWidth
                          hiddenLabel
                          size="small"
                          name='email'
                          variant="standard"
                          placeholder="your@email.com"
                          value={formData.issue.email}
                          onChange={(e) => handleInputChange('issue', 'email', e.target.value)}
                        />
                      </div>

                      <div className="group-form">
                        <label>Screenshot (optional)</label>
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                          <div
                            className="upload-area"
                            style={{ flex: 1 }}
                            onClick={() => document.getElementById('file-upload-input-issue').click()}
                          >
                            <div className='inner-attachment'>
                              <span className='icon'>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-upload w-4 h-4 mr-2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" x2="12" y1="3" y2="15"></line></svg>
                              </span>
                              <p>Upload Screenshot</p>
                            </div>
                            <input
                              id="file-upload-input-issue"
                              type="file"
                              accept="image/png, image/jpeg, image/jpg, image/gif"
                              style={{ display: 'none' }}
                              onChange={(e) => handleFileUpload('issue', e)}
                            />
                          </div>

                          <div
                            className="upload-area"
                            style={{ flex: 1, cursor: isCapturing ? 'not-allowed' : 'pointer', opacity: isCapturing ? 0.6 : 1 }}
                            onClick={() => !isCapturing && captureScreenshot('issue')}
                          >
                            <div className='inner-attachment'>
                              <span className='icon'>
                                {isCapturing ? (
                                  <CircularProgress size={16} style={{ color: '#667eea' }} />
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-camera w-4 h-4 mr-2"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path><circle cx="12" cy="13" r="3"></circle></svg>
                                )}
                              </span>
                              <p>{isCapturing ? 'Capturing...' : 'Capture Screenshot'}</p>
                            </div>
                          </div>
                        </div>
                        {previewImage.issue && (
                          <div style={{ marginTop: '10px', position: 'relative' }}>
                            <img src={previewImage.issue} alt="Screenshot" style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }} />
                            <button
                              onClick={() => removeImage('issue')}
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                background: '#ff4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '25px',
                                height: '25px',
                                cursor: 'pointer'
                              }}
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </div>

                      {submitError && (
                        <Alert color="danger" style={{ marginTop: '15px' }}>
                          {submitError}
                        </Alert>
                      )}
                      {submitSuccess && (
                        <Alert color="success" style={{ marginTop: '15px' }}>
                          Feedback submitted successfully! Ticket ID: {ticketId}
                        </Alert>
                      )}
                      <div className="group-form">
                        <button
                          className="btn-style-new"
                          type="button"
                          onClick={() => handleSubmit('issue')}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <><CircularProgress size={16} style={{ marginRight: '8px' }} /> Submitting...</>
                          ) : (
                            <><i className="icon"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-send w-4 h-4 mr-2"><path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"></path><path d="m21.854 2.147-10.94 10.939"></path></svg></i>
                          Submit Bug Report</>
                        )}
                        </button>
                      </div>
                    </Form>
                  </div>
                </div>
              ) : null}

              {/* --- Suggestions Form --- */}
              {suggestionsTab ? (
                <div className="bug-modal">
                  <div className="title-area">
                    <h3>
                      <button className="back-btn-issue" onClick={() => { setSuggestionsTab(false); setSubmitError(''); setSubmitSuccess(false); }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-right w-4 h-4 rotate-180"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
                      </button>
                      <i className='icon'>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-lightbulb w-6 h-6 text-primary"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"></path><path d="M9 18h6"></path><path d="M10 22h4"></path></svg>
                      </i>
                      Suggestions
                    </h3>
                  </div>
                  <div className="form-area">
                    <Form>
                      <div className="group-form">
                        <label>Suggestion Title *</label>
                        <TextField
                          fullWidth
                          hiddenLabel
                          size="small"
                          name='bug'
                          variant="standard"
                          placeholder="Brief summary of your suggestion"
                          value={formData.suggestion.title}
                          onChange={(e) => handleInputChange('suggestion', 'title', e.target.value)}
                        />
                      </div>
                      <div className="group-form">
                        <label>Description *</label>
                        <TextField
                          fullWidth
                          multiline
                          rows="3"
                          hiddenLabel
                          size="small"
                          name='description'
                          variant="standard"
                          placeholder="Please describe your suggestion in detail"
                          value={formData.suggestion.description}
                          onChange={(e) => handleInputChange('suggestion', 'description', e.target.value)}
                        />
                      </div>
                      <div className="group-form">
                        <label>Email (optional)</label>
                        <TextField
                          fullWidth
                          hiddenLabel
                          size="small"
                          name='email'
                          variant="standard"
                          placeholder="your@email.com"
                          value={formData.suggestion.email}
                          onChange={(e) => handleInputChange('suggestion', 'email', e.target.value)}
                        />
                      </div>

                      <div className="group-form">
                        <label>Screenshot (optional)</label>
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                          <div
                            className="upload-area"
                            style={{ flex: 1 }}
                            onClick={() => document.getElementById('file-upload-input-suggestion').click()}
                          >
                            <div className='inner-attachment'>
                              <span className='icon'>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-upload w-4 h-4 mr-2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" x2="12" y1="3" y2="15"></line></svg>
                              </span>
                              <p>Upload Screenshot</p>
                            </div>
                            <input
                              id="file-upload-input-suggestion"
                              type="file"
                              accept="image/png, image/jpeg, image/jpg, image/gif"
                              style={{ display: 'none' }}
                              onChange={(e) => handleFileUpload('suggestion', e)}
                            />
                          </div>

                          <div
                            className="upload-area"
                            style={{ flex: 1, cursor: isCapturing ? 'not-allowed' : 'pointer', opacity: isCapturing ? 0.6 : 1 }}
                            onClick={() => !isCapturing && captureScreenshot('suggestion')}
                          >
                            <div className='inner-attachment'>
                              <span className='icon'>
                                {isCapturing ? (
                                  <CircularProgress size={16} style={{ color: '#667eea' }} />
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-camera w-4 h-4 mr-2"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path><circle cx="12" cy="13" r="3"></circle></svg>
                                )}
                              </span>
                              <p>{isCapturing ? 'Capturing...' : 'Capture Screenshot'}</p>
                            </div>
                          </div>
                        </div>
                        {previewImage.suggestion && (
                          <div style={{ marginTop: '10px', position: 'relative' }}>
                            <img src={previewImage.suggestion} alt="Screenshot" style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }} />
                            <button
                              onClick={() => removeImage('suggestion')}
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                background: '#ff4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '25px',
                                height: '25px',
                                cursor: 'pointer'
                              }}
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </div>

                      {submitError && (
                        <Alert color="danger" style={{ marginTop: '15px' }}>
                          {submitError}
                        </Alert>
                      )}
                      {submitSuccess && (
                        <Alert color="success" style={{ marginTop: '15px' }}>
                          Feedback submitted successfully! Reference ID: {ticketId}
                        </Alert>
                      )}
                      <div className="group-form">
                        <button
                          className="btn-style-new"
                          type="button"
                          onClick={() => handleSubmit('suggestion')}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <><CircularProgress size={16} style={{ marginRight: '8px' }} /> Submitting...</>
                          ) : (
                            <><i className="icon"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-send w-4 h-4 mr-2"><path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"></path><path d="m21.854 2.147-10.94 10.939"></path></svg></i>
                          Submit Suggestion</>
                        )}
                        </button>
                      </div>
                    </Form>
                  </div>
                </div>
              ) : null}

              {/* --- Others Form --- */}
              {othersTab ? (
                <div className="bug-modal">
                  <div className="title-area">
                    <h3>
                      <button className="back-btn-issue" onClick={() => { setOtherTab(false); setSubmitError(''); setSubmitSuccess(false); }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-right w-4 h-4 rotate-180"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
                      </button>
                      <i className='icon'>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-message-circle w-6 h-6 text-primary"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path></svg>
                      </i>
                      Others
                    </h3>
                  </div>
                  <div className="form-area">
                    <Form>
                      <div className="group-form">
                        <label>Subject *</label>
                        <TextField
                          fullWidth
                          hiddenLabel
                          size="small"
                          name='bug'
                          variant="standard"
                          placeholder="What is this about?"
                          value={formData.others.title}
                          onChange={(e) => handleInputChange('others', 'title', e.target.value)}
                        />
                      </div>
                      <div className="group-form">
                        <label>Description *</label>
                        <TextField
                          fullWidth
                          multiline
                          rows="3"
                          hiddenLabel
                          size="small"
                          name='description'
                          variant="standard"
                          placeholder="Please provide details"
                          value={formData.others.description}
                          onChange={(e) => handleInputChange('others', 'description', e.target.value)}
                        />
                      </div>
                      <div className="group-form">
                        <label>Email (optional)</label>
                        <TextField
                          fullWidth
                          hiddenLabel
                          size="small"
                          name='email'
                          variant="standard"
                          placeholder="your@email.com"
                          value={formData.others.email}
                          onChange={(e) => handleInputChange('others', 'email', e.target.value)}
                        />
                      </div>

                      <div className="group-form">
                        <label>Screenshot (optional)</label>
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                          <div
                            className="upload-area"
                            style={{ flex: 1 }}
                            onClick={() => document.getElementById('file-upload-input-others').click()}
                          >
                            <div className='inner-attachment'>
                              <span className='icon'>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-upload w-4 h-4 mr-2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" x2="12" y1="3" y2="15"></line></svg>
                              </span>
                              <p>Upload Screenshot</p>
                            </div>
                            <input
                              id="file-upload-input-others"
                              type="file"
                              accept="image/png, image/jpeg, image/jpg, image/gif"
                              style={{ display: 'none' }}
                              onChange={(e) => handleFileUpload('others', e)}
                            />
                          </div>

                          <div
                            className="upload-area"
                            style={{ flex: 1, cursor: isCapturing ? 'not-allowed' : 'pointer', opacity: isCapturing ? 0.6 : 1 }}
                            onClick={() => !isCapturing && captureScreenshot('others')}
                          >
                            <div className='inner-attachment'>
                              <span className='icon'>
                                {isCapturing ? (
                                  <CircularProgress size={16} style={{ color: '#667eea' }} />
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-camera w-4 h-4 mr-2"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path><circle cx="12" cy="13" r="3"></circle></svg>
                                )}
                              </span>
                              <p>{isCapturing ? 'Capturing...' : 'Capture Screenshot'}</p>
                            </div>
                          </div>
                        </div>
                        {previewImage.others && (
                          <div style={{ marginTop: '10px', position: 'relative' }}>
                            <img src={previewImage.others} alt="Screenshot" style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }} />
                            <button
                              onClick={() => removeImage('others')}
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                background: '#ff4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '25px',
                                height: '25px',
                                cursor: 'pointer'
                              }}
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </div>

                      {submitError && (
                        <Alert color="danger" style={{ marginTop: '15px' }}>
                          {submitError}
                        </Alert>
                      )}
                      {submitSuccess && (
                        <Alert color="success" style={{ marginTop: '15px' }}>
                          Feedback submitted successfully! Reference ID: {ticketId}
                        </Alert>
                      )}
                      <div className="group-form">
                        <button
                          className="btn-style-new"
                          type="button"
                          onClick={() => handleSubmit('others')}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <><CircularProgress size={16} style={{ marginRight: '8px' }} /> Submitting...</>
                          ) : (
                            <><i className="icon"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-send w-4 h-4 mr-2"><path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"></path><path d="m21.854 2.147-10.94 10.939"></path></svg></i>
                          Submit Feedback</>
                        )}
                        </button>
                      </div>
                    </Form>
                  </div>
                </div>
              ) : null}

              {/* --- Main Menu --- */}
              {!issuesTab && !suggestionsTab && !othersTab && (
                <div className="bug-modal">
                  <div className="title-area">
                    <h3>
                      <i className='icon'>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-message-circle w-6 h-6 text-primary" data-lov-id="src/components/BugReportWidget.tsx:118:20" data-lov-name="MessageCircle" data-component-path="src/components/BugReportWidget.tsx" data-component-line="118" data-component-file="BugReportWidget.tsx" data-component-name="MessageCircle" data-component-content="%7B%22className%22%3A%22w-6%20h-6%20text-primary%22%7D"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path></svg>
                      </i>
                      How can we help you?
                    </h3>
                    <p>Choose the type of feedback you'd like to provide:</p>
                  </div>
                  <div className='report-area'>

                    <button className='report-bug' onClick={() => setIssuesTab(true)}>
                      <div className='icon'>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bug w-6 h-6 text-primary" data-lov-id="src/components/BugReportWidget.tsx:160:24" data-lov-name="IconComponent" data-component-path="src/components/BugReportWidget.tsx" data-component-line="160" data-component-file="BugReportWidget.tsx" data-component-name="IconComponent" data-component-content="%7B%22className%22%3A%22w-6%20h-6%20text-primary%22%7D"><path d="m8 2 1.88 1.88"></path><path d="M14.12 3.88 16 2"></path><path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1"></path><path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6"></path><path d="M12 20v-9"></path><path d="M6.53 9C4.6 8.8 3 7.1 3 5"></path><path d="M6 13H2"></path><path d="M3 21c0-2.1 1.7-3.9 3.8-4"></path><path d="M20.97 5c0 2.1-1.6 3.8-3.5 4"></path><path d="M22 13h-4"></path><path d="M17.2 17c2.1.1 3.8 1.9 3.8 4"></path></svg>
                      </div>
                      <div className='text-box'>
                        <h4>Issue</h4>
                        <p>Report a problem or bug</p>
                      </div>
                    </button>
                    <button className='report-bug' onClick={() => setSuggestionsTab(true)}>
                      <div className='icon'>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-lightbulb w-6 h-6 text-primary" data-lov-id="src/components/BugReportWidget.tsx:160:24" data-lov-name="IconComponent" data-component-path="src/components/BugReportWidget.tsx" data-component-line="160" data-component-file="BugReportWidget.tsx" data-component-name="IconComponent" data-component-content="%7B%22className%22%3A%22w-6%20h-6%20text-primary%22%7D"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"></path><path d="M9 18h6"></path><path d="M10 22h4"></path></svg>
                      </div>
                      <div className='text-box'>
                        <h4>Suggestions</h4>
                        <p>Share your ideas for improvement</p>
                      </div>
                    </button>
                    <button className='report-bug' onClick={() => setOtherTab(true)}>
                      <div className='icon'>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-message-circle w-6 h-6 text-primary" data-lov-id="src/components/BugReportWidget.tsx:160:24" data-lov-name="IconComponent" data-component-path="src/components/BugReportWidget.tsx" data-component-line="160" data-component-file="BugReportWidget.tsx" data-component-name="IconComponent" data-component-content="%7B%22className%22%3A%22w-6%20h-6%20text-primary%22%7D"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path></svg>
                      </div>
                      <div className='text-box'>
                        <h4>Others</h4>
                        <p>General feedback or questions</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Selection Overlay */}
      {showSelectionOverlay && (
        <SelectionOverlay
          onComplete={handleSelectionComplete}
          onCancel={handleSelectionCancel}
        />
      )}
    </>
  )
}

// Selection Overlay Component - Immediate drag-to-select
const SelectionOverlay = ({ onComplete, onCancel }) => {
  const [selectionArea, setSelectionArea] = useState({ startX: 0, startY: 0, endX: 0, endY: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = (e) => {
    const x = e.clientX;
    const y = e.clientY;
    setSelectionArea({ startX: x, startY: y, endX: x, endY: y });
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setSelectionArea(prev => ({
      ...prev,
      endX: e.clientX,
      endY: e.clientY
    }));
  };

  const handleMouseUp = () => {
    if (!isDragging) return;

    setIsDragging(false);

    const x = Math.min(selectionArea.startX, selectionArea.endX);
    const y = Math.min(selectionArea.startY, selectionArea.endY);
    const width = Math.abs(selectionArea.endX - selectionArea.startX);
    const height = Math.abs(selectionArea.endY - selectionArea.startY);

    // Check if selection is valid (not too small)
    if (width < 10 || height < 10) {
      EventBus.publish('error', 'Please select a larger area');
      return;
    }

    // Return selected area
    onComplete({ x, y, width, height });
  };

  // Handle ESC key to cancel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const selectionStyle = {
    left: Math.min(selectionArea.startX, selectionArea.endX) + 'px',
    top: Math.min(selectionArea.startY, selectionArea.endY) + 'px',
    width: Math.abs(selectionArea.endX - selectionArea.startX) + 'px',
    height: Math.abs(selectionArea.endY - selectionArea.startY) + 'px',
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        zIndex: 99999,
        cursor: 'crosshair',
        userSelect: 'none'
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* Instructions */}
      <div style={{
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: '15px 30px',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '500',
        zIndex: 100000,
        pointerEvents: 'none',
        textAlign: 'center'
      }}>
        <div>Click and drag to select the area to capture</div>
        <div style={{ fontSize: '12px', marginTop: '5px', opacity: 0.8 }}>Press ESC to cancel</div>
      </div>

      {/* Selection rectangle */}
      {isDragging && (
        <div style={{
          position: 'absolute',
          border: '3px solid #667eea',
          backgroundColor: 'rgba(102, 126, 234, 0.2)',
          pointerEvents: 'none',
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
          ...selectionStyle
        }} />
      )}

      {/* Cancel button */}
      <button
        onClick={onCancel}
        style={{
          position: 'fixed',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '12px 30px',
          backgroundColor: '#ff4444',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: 'pointer',
          zIndex: 100000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}
      >
        Cancel (ESC)
      </button>
    </div>
  );
};

export default AddDealModal;